/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer, Radio, Button, message, Spin, Input, Popconfirm,
} from 'antd';
import axios from 'axios';
import JsonEditor from '../../../../../../pages/private/subpages/dictionaries/components/JsonEditor';

import {
  ContentWrapper,
  ControlsWrapper,
} from './styles';

import Preview from './components/Preview';
import DictionaryBlock from './components/DictionaryBlock';
import AdditionalDictionaryContent from './components/AdditionalDictionaryContent';
import DictionaryCardsGrid from './components/DictionaryCardsGrid';
import SingleDictionaryContent from './components/SingleDictionaryContent';

function DictionarySelector({
  isOpen, onClose, serviceId, serviceName, serviceData: serviceDataProp, preloader, fetchServices,
}) {
  const [localServiceData, setLocalServiceData] = useState(null);
  const serviceData = localServiceData || serviceDataProp;
  const [dictionaries, setDictionaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1 - выбор справочника, 2 - настройки и предпросмотр
  const [selectedDictionary, setSelectedDictionary] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [inputMode, setInputMode] = useState('dictionary'); // 'dictionary' или 'custom'
  const [customValue, setCustomValue] = useState('');
  const [targetField, setTargetField] = useState('external_key'); // 'external_key' или 'external_data'
  const [dataType, setDataType] = useState('key'); // 'key' или 'value'
  // Для external_data: массив выбранных элементов из разных справочников
  // Формат: [{ dictionaryId, dictionaryName, key, value, dataType, integrationKey }]
  const [selectedItems, setSelectedItems] = useState([]);
  // Для управления добавлением дополнительных справочников
  const [showAddMore, setShowAddMore] = useState(false);
  const [additionalDictionary, setAdditionalDictionary] = useState(null);
  const [additionalSearchText, setAdditionalSearchText] = useState('');
  // Массив добавленных справочников с их состоянием
  // Формат: [{ dictionary, isExpanded, searchText }]
  const [addedDictionaries, setAddedDictionaries] = useState([]);
  // Состояние развернутости основного справочника для external_data
  const [mainExpanded, setMainExpanded] = useState(true);
  const jsonEditorRef = useRef(null);

  // Вычисляем начальное значение для JSON-редактора
  const getInitialJsonValue = () => {
    if (selectedDictionary?.external_integration_key) {
      return { [selectedDictionary.external_integration_key]: '' };
    }
    return {};
  };

  const [jsonValue, setJsonValue] = useState(getInitialJsonValue());

  const fetchDictionaries = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/dictionaries');
      if (response.data.status === 'success') {
        // Фильтруем только справочники типа 'queues' и 'general'
        const filtered = response.data.data.filter(
          (dict) => dict.type === 'queues' || dict.type === 'general',
        );
        setDictionaries(filtered);
      }
    } catch (error) {
      message.error('Ошибка при загрузке справочников');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchDictionaries();
      setLocalServiceData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Перезагрузка данных текущей услуги (без закрытия drawer)
  const refreshServiceData = async () => {
    try {
      const token = window.localStorage.getItem('token');
      const response = await axios.post(`/server/api/queue/?action=get&token=${token}`, {
        data: {},
      });
      if (response.data?.data) {
        const services = response.data.data;
        const updated = services.find(s => String(s.id) === String(serviceId));
        if (updated) {
          setLocalServiceData(updated);
        }
      }
    } catch (e) {
      console.error('Ошибка при обновлении данных услуги:', e);
    }
  };

  // Загружаем существующие данные при открытии модалки или обновлении serviceData
  useEffect(() => {
    if (isOpen && dictionaries.length > 0 && serviceData) {
      const hasExternalKey = serviceData.external_key;
      const rawExternalData = serviceData.external_data;
      // Пустой объект {} не считаем за данные
      let hasExternalData = false;
      if (rawExternalData) {
        try {
          const parsed = typeof rawExternalData === 'string' ? JSON.parse(rawExternalData) : rawExternalData;
          hasExternalData = parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0;
        } catch (e) {
          hasExternalData = false;
        }
      }

      if (hasExternalKey || hasExternalData) {
        setInputMode('dictionary');
        let newSelectedDictionary = null;

        // Обрабатываем external_key
        if (hasExternalKey) {
          setTargetField('external_key');
          let foundDict = null;
          let foundKey = null;
          let foundDataType = 'key';

          for (const dict of dictionaries) {
            const content = dict.content || {};
            if (Object.keys(content).includes(hasExternalKey)) {
              foundDict = dict;
              foundKey = hasExternalKey;
              foundDataType = 'key';
              break;
            }
            for (const [key, value] of Object.entries(content)) {
              if (value === hasExternalKey) {
                foundDict = dict;
                foundKey = key;
                foundDataType = 'value';
                break;
              }
            }
            if (foundDict) break;
          }

          if (foundDict) {
            newSelectedDictionary = foundDict;
            setSelectedDictionary(foundDict);
            setSelectedKey(foundKey);
            setDataType(foundDataType);
          } else {
            setSelectedKey(null);
          }
        } else {
          setSelectedKey(null);
        }

        // Обрабатываем external_data
        if (hasExternalData) {
          setTargetField('external_data');
          try {
            const parsedData = typeof rawExternalData === 'string'
              ? JSON.parse(rawExternalData)
              : rawExternalData;

            const items = [];
            const addedDicts = [];

            for (const [integrationKey, value] of Object.entries(parsedData)) {
              const dict = dictionaries.find(d => d.external_integration_key === integrationKey);

              if (dict) {
                const content = dict.content || {};
                let foundKey = null;
                let itemDataType = 'key';

                if (Object.keys(content).includes(value)) {
                  foundKey = value;
                  itemDataType = 'key';
                } else {
                  for (const [key, val] of Object.entries(content)) {
                    if (val === value) {
                      foundKey = key;
                      itemDataType = 'value';
                      break;
                    }
                  }
                }

                if (foundKey) {
                  const item = {
                    dictionaryId: dict.id,
                    dictionaryName: dict.name,
                    key: foundKey,
                    value: content[foundKey],
                    dataType: itemDataType,
                    integrationKey: dict.external_integration_key,
                  };

                  if (items.length === 0) {
                    newSelectedDictionary = dict;
                    setSelectedDictionary(dict);
                    items.push(item);
                  } else {
                    items.push(item);
                    addedDicts.push({
                      dictionary: dict,
                      isExpanded: true,
                      searchText: '',
                    });
                  }
                }
              }
            }

            setSelectedItems(items);
            setAddedDictionaries(addedDicts);
          } catch (error) {
            console.error('Ошибка парсинга external_data:', error);
            setSelectedItems([]);
            setAddedDictionaries([]);
          }
        } else {
          setSelectedItems([]);
          setAddedDictionaries([]);
        }

        if (!newSelectedDictionary) {
          setSelectedDictionary(null);
        }

        setCurrentStep(2);
      } else {
        // Нет привязанных данных — сбрасываем на шаг 1
        setSelectedDictionary(null);
        setSelectedKey(null);
        setSelectedItems([]);
        setAddedDictionaries([]);
        setCurrentStep(1);
      }
    }
  }, [isOpen, dictionaries, serviceData]);


  const handleDictionarySelect = (dict) => {
    setSelectedDictionary(dict);
    setCurrentStep(2);
    setSelectedKey(null);
    setSearchText('');
    setInputMode('dictionary');
    setCustomValue('');
    // Инициализируем JSON-редактор с ключом интеграции
    if (dict.external_integration_key) {
      setJsonValue({ [dict.external_integration_key]: '' });
    } else {
      setJsonValue({});
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
    setSelectedDictionary(null);
    setSelectedKey(null);
    setSearchText('');
    setInputMode('dictionary');
    setCustomValue('');
  };

  const handleClose = () => {
    setCurrentStep(1);
    setSelectedDictionary(null);
    setSelectedKey(null);
    setSearchText('');
    setInputMode('dictionary');
    setCustomValue('');
    setTargetField('external_key');
    setSelectedItems([]);
    setShowAddMore(false);
    setAdditionalDictionary(null);
    setAdditionalSearchText('');
    setAddedDictionaries([]);
    setLocalServiceData(null);
    onClose();
  };

  const handleKeySelect = (key) => {
    setSelectedKey(key);

    // Для external_data автоматически добавляем выбранный элемент в список
    if (targetField === 'external_data' && selectedDictionary) {
      handleAddItem(selectedDictionary, key, dataType);
    }
  };

  // Синхронизация при переключении между external_key и external_data
  useEffect(() => {
    if (targetField === 'external_data' && selectedKey && selectedDictionary) {
      // При переключении на external_data добавляем выбранный элемент из external_key
      const alreadyExists = selectedItems.some(
        (item) => item.dictionaryId === selectedDictionary.id
      );
      if (!alreadyExists) {
        handleAddItem(selectedDictionary, selectedKey, dataType);
      }
    }
  }, [targetField]);

  // Добавить элемент из справочника в список выбранных (для external_data)
  const handleAddItem = (dictionary, key, itemDataType) => {
    const content = dictionary.content || {};
    const newItem = {
      dictionaryId: dictionary.id,
      dictionaryName: dictionary.name,
      key,
      value: content[key],
      dataType: itemDataType,
      integrationKey: dictionary.external_integration_key || key,
    };

    // Проверяем, есть ли уже элемент из этого справочника
    const existingIndex = selectedItems.findIndex(
      (item) => item.dictionaryId === dictionary.id
    );

    // Если элемент из этого справочника уже выбран, заменяем его
    if (existingIndex !== -1) {
      const updated = [...selectedItems];
      updated[existingIndex] = newItem;
      setSelectedItems(updated);
    } else {
      // Иначе добавляем новый
      setSelectedItems([...selectedItems, newItem]);
    }

    // Если это дополнительный справочник, добавляем его в список
    if (dictionary.id !== selectedDictionary?.id && additionalDictionary) {
      const alreadyAdded = addedDictionaries.some(d => d.dictionary.id === dictionary.id);
      if (!alreadyAdded) {
        setAddedDictionaries([...addedDictionaries, {
          dictionary,
          isExpanded: true,
          searchText: additionalSearchText
        }]);
        // Закрываем выбор дополнительного справочника
        setShowAddMore(false);
        setAdditionalDictionary(null);
        setAdditionalSearchText('');
      }
    }
  };

  // Удалить элемент из списка выбранных
  const handleRemoveItem = (index) => {
    setSelectedItems(selectedItems.filter((_, i) => i !== index));
  };

  // Изменить тип данных для выбранного элемента
  const handleChangeItemDataType = (index, newDataType) => {
    const updated = [...selectedItems];
    updated[index].dataType = newDataType;
    setSelectedItems(updated);
  };

  const getValueToSave = () => {
    if (inputMode === 'custom') {
      // Кастомное значение
      if (targetField === 'external_key') {
        return customValue;
      }
      // Для external_data используем состояние jsonValue
      return jsonValue;
    }

    if (targetField === 'external_key') {
      // Для external_key работаем с одним справочником
      if (!selectedKey || !selectedDictionary) return null;
      const content = selectedDictionary.content || {};

      if (dataType === 'key') {
        return selectedKey;
      }
      return content[selectedKey];
    }

    // Для external_data объединяем данные из всех выбранных элементов
    if (selectedItems.length === 0) return null;

    const result = {};
    selectedItems.forEach((item) => {
      const key = item.integrationKey || item.key;
      const value = item.dataType === 'key' ? item.key : item.value;
      result[key] = value;
    });

    return result;
  };

  // linkedItem: { field: 'external_key'|'external_data', integrationKey?, dictionary? }
  const handleDetach = async (linkedItem) => {
    preloader(true);
    try {
      const updateData = {};

      if (serviceData) {
        updateData.position = serviceData.position;
        updateData.name = serviceData.name;
        updateData.fullname = serviceData.fullname;
        updateData.prefix = serviceData.prefix;
        updateData.prior = serviceData.prior;
        updateData.workWeek = serviceData.workWeek;
      }

      // Сохраняем существующие значения по умолчанию
      if (serviceData?.external_key) {
        updateData.external_key = serviceData.external_key;
      }
      // external_data NOT NULL в БД — всегда отправляем объект
      try {
        const existing = serviceData?.external_data;
        updateData.external_data = existing
          ? (typeof existing === 'string' ? JSON.parse(existing) : existing)
          : {};
      } catch (e) {
        updateData.external_data = {};
      }

      if (linkedItem.field === 'external_key') {
        // Очищаем external_key
        updateData.external_key = null;
      } else if (linkedItem.field === 'external_data' && linkedItem.integrationKey) {
        // Удаляем конкретный ключ из external_data
        try {
          const parsedData = typeof serviceData.external_data === 'string'
            ? JSON.parse(serviceData.external_data)
            : serviceData.external_data;

          delete parsedData[linkedItem.integrationKey];

          if (Object.keys(parsedData).length > 0) {
            updateData.external_data = parsedData;
          } else {
            updateData.external_data = {};
          }
        } catch (e) {
          updateData.external_data = {};
        }
      }

      const token = window.localStorage.getItem('token');
      await axios.post(`/server/api/queue/${serviceId}/?action=update&token=${token}`, { data: updateData });
      message.success('Значение успешно откреплено');
      if (fetchServices) fetchServices();
      // Не закрываем drawer, обновляем данные на месте
      await refreshServiceData();
    } catch (error) {
      message.error('Ошибка при откреплении справочника');
    } finally {
      preloader(false);
    }
  };

  const handleSave = async () => {
    preloader(true);
    try {
      console.log('=== handleSave START ===');
      console.log('serviceData from props:', serviceData);

      const updateData = {};

      // Если есть данные очереди, начинаем с них
      if (serviceData) {
        console.log('Adding service data to updateData');
        updateData.position = serviceData.position;
        updateData.name = serviceData.name;
        updateData.fullname = serviceData.fullname;
        updateData.prefix = serviceData.prefix;
        updateData.prior = serviceData.prior;
        updateData.workWeek = serviceData.workWeek;
      } else {
        console.warn('No service data available, sending only external fields');
      }

      // Добавляем external_key если он выбран
      if (inputMode === 'dictionary' && selectedKey && selectedDictionary) {
        const content = selectedDictionary.content || {};
        const keyValue = dataType === 'key' ? selectedKey : content[selectedKey];
        updateData.external_key = keyValue;
      } else if (inputMode === 'custom' && customValue) {
        updateData.external_key = customValue;
      } else if (serviceData?.external_key) {
        // Сохраняем существующий external_key если не выбран новый
        updateData.external_key = serviceData.external_key;
      }

      // Добавляем external_data если выбраны элементы
      // external_data NOT NULL в БД — всегда отправляем объект
      if (selectedItems.length > 0) {
        const result = {};
        selectedItems.forEach((item) => {
          const key = item.integrationKey || item.key;
          const value = item.dataType === 'key' ? item.key : item.value;
          result[key] = value;
        });
        updateData.external_data = result;
      } else if (inputMode === 'custom' && targetField === 'external_data' && jsonValue) {
        updateData.external_data = jsonValue;
      } else if (serviceData?.external_data) {
        try {
          updateData.external_data = typeof serviceData.external_data === 'string'
            ? JSON.parse(serviceData.external_data)
            : serviceData.external_data;
        } catch (e) {
          updateData.external_data = {};
        }
      }
      if (!updateData.external_data) {
        updateData.external_data = {};
      }

      // Проверяем что есть что сохранять
      if (!updateData.external_key && !updateData.external_data && !serviceData) {
        message.warning('Выберите элемент из справочника или введите значение');
        return;
      }

      console.log('updateData', updateData);
      const token = window.localStorage.getItem('token');
      await axios.post(`/server/api/queue/${serviceId}/?action=update&token=${token}`, { data: updateData });
      message.success('Данные успешно сохранены');
      if (fetchServices) fetchServices();
      handleClose();
    } catch (error) {
      message.error('Ошибка при сохранении данных');
    } finally {
      preloader(false);
    }
  };

  const renderPreview = () => {
    const valueToSave = getValueToSave();

    return (
      <Preview
        inputMode={inputMode}
        targetField={targetField}
        selectedKey={selectedKey}
        selectedDictionary={selectedDictionary}
        selectedItems={selectedItems}
        dataType={dataType}
        customValue={customValue}
        valueToSave={valueToSave}
      />
    );
  };

  // Рендер одного справочника с возможностью сворачивания
  const renderDictionaryBlock = (dict, dictSearchText, setDictSearchText, isExpanded, toggleExpanded) => (
    <DictionaryBlock
      dict={dict}
      searchText={dictSearchText}
      setSearchText={setDictSearchText}
      isExpanded={isExpanded}
      toggleExpanded={toggleExpanded}
      selectedItems={selectedItems}
      handleAddItem={handleAddItem}
      handleChangeItemDataType={handleChangeItemDataType}
    />
  );

  // Рендер содержимого дополнительного справочника для выбора
  const renderAdditionalDictionaryContent = () => {
    if (!additionalDictionary) return null;

    return (
      <AdditionalDictionaryContent
        additionalDictionary={additionalDictionary}
        additionalSearchText={additionalSearchText}
        setAdditionalSearchText={setAdditionalSearchText}
        selectedItems={selectedItems}
        handleAddItem={handleAddItem}
        handleChangeItemDataType={handleChangeItemDataType}
        onCancel={() => {
          setShowAddMore(false);
          setAdditionalDictionary(null);
          setAdditionalSearchText('');
        }}
      />
    );
  };

  // Рендер списка доступных справочников для добавления (карточками как на шаге 1)
  const renderDictionarySelector = () => {
    // Исключаем уже использованные справочники
    const usedDictionaryIds = selectedItems.map(item => item.dictionaryId);
    const availableDictionaries = dictionaries.filter(
      dict => !usedDictionaryIds.includes(dict.id) && dict.id !== selectedDictionary?.id
    );

    if (availableDictionaries.length === 0) {
      return (
        <div style={{ padding: 16, textAlign: 'center', color: '#999' }}>
          Все доступные справочники уже использованы
        </div>
      );
    }

    return (
      <div style={{ marginTop: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h4 style={{ margin: 0 }}>Выберите справочник для добавления:</h4>
          <Button size="small" onClick={() => setShowAddMore(false)}>
            Отмена
          </Button>
        </div>
        <DictionaryCardsGrid
          dictionaries={availableDictionaries}
          onSelect={setAdditionalDictionary}
          showIntegrationKey
        />
      </div>
    );
  };

  // Рендер для external_data - основной справочник + возможность добавления
  const renderExternalDataContent = () => {
    return (
      <div>
        {/* Основной справочник с renderDictionaryBlock */}
        {renderDictionaryBlock(
          selectedDictionary,
          searchText,
          setSearchText,
          mainExpanded,
          () => setMainExpanded(!mainExpanded)
        )}

        {/* Добавленные справочники */}
        {addedDictionaries.map((item, index) =>
          renderDictionaryBlock(
            item.dictionary,
            item.searchText,
            (text) => {
              const updated = [...addedDictionaries];
              updated[index].searchText = text;
              setAddedDictionaries(updated);
            },
            item.isExpanded,
            () => {
              const updated = [...addedDictionaries];
              updated[index].isExpanded = !updated[index].isExpanded;
              setAddedDictionaries(updated);
            },
          )
        )}

        {/* Кнопка добавления дополнительного справочника */}
        {!showAddMore && !additionalDictionary && (
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Button
              type="dashed"
              icon={<span style={{ marginRight: 4 }}>+</span>}
              onClick={() => setShowAddMore(true)}
            >
              Добавить значение из другого справочника
            </Button>
          </div>
        )}

        {/* Выбор дополнительного справочника */}
        {showAddMore && !additionalDictionary && renderDictionarySelector()}

        {/* Содержимое дополнительного справочника для выбора */}
        {additionalDictionary && renderAdditionalDictionaryContent()}
      </div>
    );
  };

  const renderDictionaryContent = () => {
    if (!selectedDictionary) return null;

    return (
      <SingleDictionaryContent
        selectedDictionary={selectedDictionary}
        searchText={searchText}
        setSearchText={setSearchText}
        selectedKey={selectedKey}
        handleKeySelect={handleKeySelect}
        dataType={dataType}
        setDataType={setDataType}
      />
    );
  };

  const renderStep1 = () => (
    <ContentWrapper>
      <h3>Шаг 1: Выберите справочник</h3>
      <DictionaryCardsGrid
        dictionaries={dictionaries}
        onSelect={handleDictionarySelect}
      />
    </ContentWrapper>
  );

  // Блок привязанных значений с кнопками открепления (отображается на шаге 2)
  const renderLinkedValues = () => {
    const items = [];

    // external_key
    if (serviceData?.external_key) {
      items.push({
        label: 'external_key',
        value: serviceData.external_key,
        onDetach: () => handleDetach({ field: 'external_key' }),
      });
    }

    // external_data
    if (serviceData?.external_data) {
      try {
        const parsedData = typeof serviceData.external_data === 'string'
          ? JSON.parse(serviceData.external_data)
          : serviceData.external_data;

        for (const [integrationKey, value] of Object.entries(parsedData)) {
          const dict = dictionaries.find(d => d.external_integration_key === integrationKey);
          items.push({
            label: dict ? dict.name : integrationKey,
            sublabel: `external_data → ${integrationKey}`,
            value: typeof value === 'object' ? JSON.stringify(value) : String(value),
            onDetach: () => handleDetach({ field: 'external_data', integrationKey }),
          });
        }
      } catch (e) {
        // ignore
      }
    }

    if (items.length === 0) return null;

    return (
      <div style={{
        marginBottom: 16,
        padding: 12,
        background: '#e6f7ff',
        border: '1px solid #91d5ff',
        borderRadius: 4,
      }}>
        <div style={{ fontWeight: 'bold', marginBottom: 8, color: '#1890ff' }}>
          Привязанные значения:
        </div>
        {items.map((item, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 0',
              borderBottom: index < items.length - 1 ? '1px solid #d6e4ff' : 'none',
            }}
          >
            <div style={{ fontSize: '13px' }}>
              <strong>{item.label}:</strong> {item.value}
              {item.sublabel && (
                <div style={{ fontSize: '11px', color: '#999' }}>{item.sublabel}</div>
              )}
            </div>
            <Popconfirm
              title={`Открепить "${item.label}"?`}
              description="Привязка будет удалена."
              onConfirm={item.onDetach}
              okText="Да, открепить"
              cancelText="Отмена"
            >
              <Button danger size="small">
                Открепить
              </Button>
            </Popconfirm>
          </div>
        ))}
      </div>
    );
  };

  const renderStep2 = () => (
    <ContentWrapper>
      <h3>
        Шаг 2: Настройки и предпросмотр
        {selectedDictionary && (
          <>
            {' '}
            <span style={{ fontSize: '14px', color: '#666' }}>
              ({selectedDictionary.name})
            </span>
          </>
        )}
      </h3>

      {/* Привязанные значения с кнопками открепления */}
      {renderLinkedValues()}

      {/* 1. Режим ввода */}
      <ControlsWrapper style={{ marginTop: 0 }}>
        <h4>1. Режим ввода</h4>
        <Radio.Group value={inputMode} onChange={(e) => {
          const newMode = e.target.value;
          if (newMode === 'custom') {
            // Инициализируем ручной ввод текущими данными
            if (serviceData?.external_key && !customValue) {
              setCustomValue(serviceData.external_key);
            }
            if (serviceData?.external_data) {
              try {
                const parsed = typeof serviceData.external_data === 'string'
                  ? JSON.parse(serviceData.external_data)
                  : serviceData.external_data;
                setJsonValue(parsed);
              } catch (err) {
                // ignore
              }
            }
          }
          setInputMode(newMode);
        }}>
          <Radio value="dictionary">Выбрать из справочника</Radio>
          <Radio value="custom">Ввести своё значение</Radio>
        </Radio.Group>
      </ControlsWrapper>

      {/* 2. Поле для сохранения */}
      <ControlsWrapper>
        <h4>2. В какое поле сохранить</h4>
        <Radio.Group value={targetField} onChange={(e) => setTargetField(e.target.value)}>
          <Radio value="external_key">
            <strong>external_key</strong>
            {' '}
            - сохранить ключ или значение
          </Radio>
          <Radio value="external_data">
            <strong>external_data</strong>
            {' '}
            - сохранить объект (можно выбрать из нескольких справочников)
          </Radio>
        </Radio.Group>
      </ControlsWrapper>

      {/* Контент: выбор из справочника или ввод */}
      {inputMode === 'dictionary' ? (
        targetField === 'external_data' ? renderExternalDataContent() : renderDictionaryContent()
      ) : (
        <div style={{ marginTop: 16 }}>
          {targetField === 'external_key' ? (
            <Input
              placeholder="Введите значение для external_key"
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
            />
          ) : (
            <div>
              <div style={{ marginBottom: 8, color: '#666' }}>
                Редактор JSON для external_data:
              </div>
              <JsonEditor
                ref={jsonEditorRef}
                value={jsonValue}
                onChange={(value) => setJsonValue(value)}
                height="300px"
              />
            </div>
          )}
        </div>
      )}

      {/* 3. Предпросмотр */}
      {renderPreview()}
    </ContentWrapper>
  );

  return (
    <Drawer
      title={`Работа со справочниками - ${serviceName}`}
      open={isOpen}
      onClose={handleClose}
      width={720}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            {currentStep === 2 && (
              <Button onClick={handleBack}>
                ← Назад
              </Button>
            )}
          </div>
          <div>
            <Button onClick={handleClose} style={{ marginRight: 8 }}>
              Отмена
            </Button>
            {currentStep === 2 && (
              <Button
                type="primary"
                onClick={handleSave}
                disabled={(() => {
                  if (inputMode === 'custom') {
                    return targetField === 'external_key' ? !customValue : false;
                  }
                  if (targetField === 'external_data') {
                    return selectedItems.length === 0;
                  }
                  return !selectedKey;
                })()}
              >
                Сохранить
              </Button>
            )}
          </div>
        </div>
      )}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
        </>
      )}
    </Drawer>
  );
}

export default DictionarySelector;
